import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";

import FileUploadArea from "./components/FileUploadArea/FileUploadArea";
import ActiveContextSelector from "../../../components/ActiveContextSelector";
import {
  pageSx,
  paperSx,
  textFieldSx,
} from "./SessionInputStep.styles";

const SessionInputView = ({
  niveles,
  areas,
  grados,
  unidades,

  nivelSeleccionado,
  areaSeleccionada,
  gradoSeleccionado,
  unidadSeleccionada,
  cantidadSesiones,

  documentoAnalizado,
  archivoPersistido,

  handleArchivoSeleccionado,
  handleDocumentoAnalizado,
  handleArchivoEliminado,

  handleNivelChange,
  handleAreaChange,
  handleGradoChange,
  handleUnidadChange,
  handleCantidadSesionesChange,

  handleContinuar,
  canGoBackToEntry,
  formularioCompleto,
  mostrarMensajeSinUnidades,

  loadingCursos,
  loadingGrados,
  loadingUnidades,

  onBack,
}) => {
  const navigate = useNavigate();

  if (!niveles.length) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        mt={10}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={pageSx}>
      <Container maxWidth="xxl">
        <Box
          sx={{
            mb: 2,
            textAlign: "initial",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              color: "#8f8f8f",
              letterSpacing: "-0.02em",
              mb: 1,
            }}
          >
            Recopilación de Datos
          </Typography>
        </Box>

        <Paper elevation={0} sx={paperSx}>
          <Box
            sx={{
              maxWidth: 700,
              margin: "0 auto",
              mb: 1,
            }}
          >
            <ActiveContextSelector
              title="Contexto Institucional"
              helperText="Este contexto se utilizará para generar y descargar el SAC de la institución seleccionada."
            />
          </Box>

          {mostrarMensajeSinUnidades ? (
            <Box
              sx={{
                mt: 4,
                textAlign: "center",
              }}
            >
              <Alert
                severity="warning"
                variant="outlined"
                sx={{
                  borderRadius: "16px",
                  p: 3,
                  textAlign: "left",
                }}
              >
                <AlertTitle
                  sx={{ fontWeight: 700 }}
                >
                  Unidades no encontradas
                </AlertTitle>

                No se han detectado unidades
                didácticas. Por favor,{" "}
                <strong>
                  contacte con el docente
                  administrador
                </strong>{" "}
                para que genere las unidades antes
                de continuar.
              </Alert>

              <Stack
                direction="row"
                spacing={2}
                justifyContent="center"
                sx={{ mt: 5 }}
              >
                <Button
                  variant="contained"
                  onClick={() => navigate("/")}
                  sx={{
                    borderRadius: "50px",
                    px: 4,
                    bgcolor: "#00c7db",
                  }}
                >
                  Volver al Inicio
                </Button>
              </Stack>
            </Box>
          ) : (
            <Box>
              <Box sx={{ mb: 4 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 1,
                  }}
                >
                  <MenuBookRoundedIcon
                    sx={{ color: "#00c7db" }}
                  />

                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "#1e293b",
                      fontSize: "1.05rem",
                    }}
                  >
                    Diseño Curricular
                  </Typography>
                </Box>

                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      fullWidth
                      label="Nivel Educativo"
                      value={nivelSeleccionado}
                      onChange={
                        handleNivelChange
                      }
                      disabled={loadingCursos}
                      sx={textFieldSx}
                    >
                      {niveles.map((item) => (
                        <MenuItem
                          key={item.id}
                          value={item.id}
                        >
                          {item.nombre}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      fullWidth
                      label="Área Temática"
                      value={areaSeleccionada}
                      onChange={
                        handleAreaChange
                      }
                      disabled={
                        !nivelSeleccionado ||
                        loadingCursos
                      }
                      sx={textFieldSx}
                    >
                      {areas.map((item) => (
                        <MenuItem
                          key={item.id}
                          value={item.id}
                        >
                          {item.nombre}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                </Grid>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label="Grado Escolar"
                    value={gradoSeleccionado}
                    onChange={
                      handleGradoChange
                    }
                    disabled={
                      !areaSeleccionada ||
                      loadingGrados
                    }
                    sx={textFieldSx}
                  >
                    {grados.map((item) => (
                      <MenuItem
                        key={item.id}
                        value={item.id}
                      >
                        {item.descripcion}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label="Unidad de Aprendizaje"
                    value={unidadSeleccionada}
                    onChange={(event) =>
                      handleUnidadChange(
                        Number(
                          event.target.value
                        )
                      )
                    }
                    disabled={
                      !gradoSeleccionado ||
                      loadingUnidades
                    }
                    sx={textFieldSx}
                  >
                    {unidades.map((item) => (
                      <MenuItem
                        key={item.id}
                        value={item.id}
                        sx={{
                          whiteSpace: "normal",
                        }}
                      >
                        {item.nombreUnidad}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label="Cantidad de Sesiones"
                    value={cantidadSesiones}
                    onChange={(event) =>
                      handleCantidadSesionesChange(
                        Number(
                          event.target.value
                        )
                      )
                    }
                    /*
                     * Mientras el archivo continúe
                     * cargado, el selector permanece
                     * bloqueado, aunque cambie el grado.
                     */
                    disabled={documentoAnalizado}
                    sx={textFieldSx}
                  >
                    {[
                      1, 2, 3, 4, 5,
                      6, 7, 8, 9, 10,
                    ].map((numero) => (
                      <MenuItem
                        key={numero}
                        value={numero}
                      >
                        {numero}{" "}
                        {numero === 1
                          ? "Sesión"
                          : "Sesiones"}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>

              <FileUploadArea
                archivoPersistido={
                  archivoPersistido
                }
                onArchivoSeleccionado={
                  handleArchivoSeleccionado
                }
                onDocumentoAnalizado={
                  handleDocumentoAnalizado
                }
                onArchivoEliminado={
                  handleArchivoEliminado
                }
              />
            </Box>
          )}
        </Paper>

        {!mostrarMensajeSinUnidades && (
          <Box
            sx={{
              display: "flex",
              justifyContent:
                canGoBackToEntry
                  ? "space-between"
                  : "flex-end",
              alignItems: "center",
              mt: 1.5,
            }}
          >
            {canGoBackToEntry && (
              <Button
                variant="outlined"
                startIcon={
                  <ArrowBackIosNewRoundedIcon
                    sx={{
                      fontSize:
                        "14px !important",
                    }}
                  />
                }
                onClick={onBack}
                sx={{
                  borderRadius: "10px",
                  py: 1,
                  px: 3,
                  fontWeight: 700,
                }}
              >
                Atrás
              </Button>
            )}

            <Button
              variant="contained"
              endIcon={
                <ChevronRightRoundedIcon />
              }
              onClick={handleContinuar}
              disabled={!formularioCompleto}
              sx={{
                borderRadius: "10px",
                py: 1,
                px: 4,
                fontWeight: 700,
                "&.Mui-disabled": {
                  backgroundColor: "#cbd5e1",
                  color: "#ffffff",
                },
              }}
            >
              Continuar
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default SessionInputView;
